import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useAuth } from "../../context/AuthContext";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAlert } from "../../context/AlertContext";

const tipos = [
  { value: "CREDENCIALES", label: "Credenciales de acceso" },
  { value: "FACTURA", label: "Factura electrónica" },
  { value: "RECUPERACION_CLAVE", label: "Recuperación de clave" },
];

const initialForm = {
  id: null,
  tipo: "",
  nombre: "",
  asunto: "",
  cuerpo_texto: "",
  cuerpo_html: "",
  activo: true,
};

const EnterpriseTemplates = () => {
  const { accessToken, logout, user } = useAuth();
  const { dashboardTheme } = useDashboardTheme();
  const alert = useAlert();

  const [plantillas, setPlantillas] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const isSuper = user?.role === "super_admin";
  const permissions = user?.permissions || [];
  const canView = isSuper || permissions.includes("enterprise.view");
  const canCreate = isSuper || permissions.includes("enterprise.create");
  const canEdit = isSuper || permissions.includes("enterprise.edit");
  const canDelete = isSuper || permissions.includes("enterprise.delete");

  const isEditing = !!form.id;

  const notify = useCallback(
    (message, severity = "success") => {
      if (typeof alert?.showAlert === "function") {
        alert.showAlert(message, severity);
      }
    },
    [alert]
  );

  const getToken = useCallback(() => {
    return accessToken || localStorage.getItem("accessToken") || "";
  }, [accessToken]);

  const parseJsonSafe = async (res) => {
    try {
      return await res.json();
    } catch {
      return { error: "Respuesta inválida del servidor" };
    }
  };

  const authFetch = useCallback(
    async (url, options = {}) => {
      const token = getToken();

      const headers = {
        Accept: "application/json",
        ...(options.headers || {}),
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      return fetch(url, {
        ...options,
        headers,
      });
    },
    [getToken]
  );

  const normalizeError = (data, fallback) => {
    if (!data) return fallback;
    if (typeof data.error === "string") return data.error;
    if (typeof data.detail === "string") return data.detail;
    if (typeof data.mensaje === "string") return data.mensaje;
    if (typeof data.message === "string") return data.message;

    const values = Object.values(data.error || data || {})
      .flat()
      .map((item) => (typeof item === "string" ? item : JSON.stringify(item)))
      .join(" ");

    return values || fallback;
  };

  const loadPlantillas = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const res = await authFetch("/api/enterprise/empresa/plantillas/");
      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok) {
        setPlantillas(Array.isArray(data?.plantillas) ? data.plantillas : []);
      } else {
        notify(normalizeError(data, "No se pudo cargar las plantillas."), "error");
      }
    } catch {
      notify("Error de conexión cargando plantillas.", "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch, canView, logout, notify]);

  useEffect(() => {
    loadPlantillas();
  }, [loadPlantillas]);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: dashboardTheme.card_background,
      color: dashboardTheme.text_color,
      "& fieldset": {
        borderColor: dashboardTheme.border_color,
      },
      "&:hover fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
      "&.Mui-focused fieldset": {
        borderColor: dashboardTheme.primary_color,
      },
    },
    "& .MuiInputLabel-root": {
      color: dashboardTheme.muted_text_color,
      "&.Mui-focused": {
        color: dashboardTheme.primary_color,
      },
    },
    "& textarea": {
      color: dashboardTheme.text_color,
    },
    "& .MuiSvgIcon-root": {
      color: dashboardTheme.muted_text_color,
    },
  };

  const primaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    bgcolor: dashboardTheme.button_color,
    color: dashboardTheme.button_text_color,
    boxShadow: "none",
    px: 2.5,
    "&:hover": {
      bgcolor: dashboardTheme.button_hover_color || dashboardTheme.button_color,
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    color: dashboardTheme.text_color,
    borderColor: dashboardTheme.border_color,
    "&:hover": {
      borderColor: dashboardTheme.primary_color,
      bgcolor: `${dashboardTheme.primary_color}12`,
    },
  };

  const iconButtonSx = {
    bgcolor:
      dashboardTheme.theme_mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : `${dashboardTheme.primary_color}12`,
    "&:hover": {
      bgcolor:
        dashboardTheme.theme_mode === "dark"
          ? "rgba(255,255,255,0.1)"
          : `${dashboardTheme.primary_color}20`,
    },
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitch = (event) => {
    setForm((prev) => ({
      ...prev,
      activo: event.target.checked,
    }));
  };

  const resetForm = () => {
    setForm(initialForm);
  };

  const submit = async (event) => {
    event.preventDefault();

    if (isEditing && !canEdit) {
      notify("No tienes permisos para editar plantillas.", "error");
      return;
    }

    if (!isEditing && !canCreate) {
      notify("No tienes permisos para crear plantillas.", "error");
      return;
    }

    if (!form.tipo || !form.nombre.trim() || !form.asunto.trim()) {
      notify("Tipo, nombre y asunto son obligatorios.", "error");
      return;
    }

    try {
      setSaving(true);

      const url = form.id
        ? `/api/enterprise/empresa/plantillas/editar/${form.id}/`
        : "/api/enterprise/empresa/plantillas/crear/";

      const method = form.id ? "PUT" : "POST";

      const res = await authFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tipo: form.tipo,
          nombre: form.nombre.trim(),
          asunto: form.asunto.trim(),
          cuerpo_texto: form.cuerpo_texto || "",
          cuerpo_html: form.cuerpo_html || "",
          activo: form.activo,
        }),
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok) {
        notify(data?.mensaje || "Plantilla guardada correctamente.", "success");
        resetForm();
        await loadPlantillas();
      } else {
        notify(normalizeError(data, "No se pudo guardar la plantilla."), "error");
      }
    } catch {
      notify("Error de conexión con el servidor.", "error");
    } finally {
      setSaving(false);
    }
  };

  const editTemplate = (template) => {
    setForm({
      id: template.id || null,
      tipo: template.tipo || "",
      nombre: template.nombre || "",
      asunto: template.asunto || "",
      cuerpo_texto: template.cuerpo_texto || "",
      cuerpo_html: template.cuerpo_html || "",
      activo: template.activo !== false,
    });
  };

  const deleteTemplate = async (id) => {
    if (!canDelete) {
      notify("No tienes permisos para eliminar plantillas.", "error");
      return;
    }

    const ok = window.confirm("¿Seguro que deseas eliminar esta plantilla?");

    if (!ok) return;

    try {
      setDeletingId(id);

      const res = await authFetch(`/api/enterprise/empresa/plantillas/eliminar/${id}/`, {
        method: "DELETE",
      });

      const data = await parseJsonSafe(res);

      if (res.status === 401) {
        notify("Sesión vencida. Inicia sesión nuevamente.", "error");
        logout();
        return;
      }

      if (res.ok) {
        notify(data?.mensaje || "Plantilla eliminada correctamente.", "success");

        if (form.id === id) {
          resetForm();
        }

        await loadPlantillas();
      } else {
        notify(normalizeError(data, "No se pudo eliminar la plantilla."), "error");
      }
    } catch {
      notify("Error eliminando plantilla.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const getTipoLabel = (value) => {
    return tipos.find((item) => item.value === value)?.label || value;
  };

  if (!canView) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: "14px",
          border: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.card_background,
        }}
      >
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 600,
            color: dashboardTheme.text_color,
            mb: 0.5,
          }}
        >
          Sin permisos
        </Typography>

        <Typography
          sx={{
            fontSize: 14,
            color: dashboardTheme.muted_text_color,
          }}
        >
          No tienes permisos para ver las plantillas de correo.
        </Typography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Stack
        spacing={1.2}
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: 260 }}
      >
        <CircularProgress size={30} sx={{ color: dashboardTheme.primary_color }} />

        <Typography sx={{ fontSize: 14, color: dashboardTheme.muted_text_color }}>
          Cargando plantillas...
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: "14px",
          border: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.card_background,
          boxShadow:
            dashboardTheme.theme_mode === "dark"
              ? "none"
              : "0 4px 10px rgba(0,0,0,0.08)",
        }}
      >
        <Stack spacing={0.7} sx={{ mb: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor:
                  dashboardTheme.theme_mode === "dark"
                    ? "rgba(255,255,255,0.06)"
                    : `${dashboardTheme.primary_color}14`,
              }}
            >
              <ArticleRoundedIcon sx={{ color: dashboardTheme.primary_color }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 17, md: 18 },
                  fontWeight: 600,
                  color: dashboardTheme.text_color,
                }}
              >
                Plantillas de correo
              </Typography>

              <Typography
                sx={{
                  fontSize: 13.5,
                  color: dashboardTheme.muted_text_color,
                  lineHeight: 1.5,
                }}
              >
                Administra los correos de credenciales, facturas y recuperación de clave.
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Box component="form" onSubmit={submit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                select
                label="Tipo"
                name="tipo"
                fullWidth
                size="small"
                value={form.tipo}
                onChange={handleChange}
                disabled={saving || (isEditing && !canEdit) || (!isEditing && !canCreate)}
                required
                sx={fieldSx}
              >
                {tipos.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={7}>
              <TextField
                label="Nombre"
                name="nombre"
                fullWidth
                size="small"
                value={form.nombre}
                onChange={handleChange}
                disabled={saving || (isEditing && !canEdit) || (!isEditing && !canCreate)}
                required
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Asunto"
                name="asunto"
                fullWidth
                size="small"
                value={form.asunto}
                onChange={handleChange}
                disabled={saving || (isEditing && !canEdit) || (!isEditing && !canCreate)}
                required
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Texto plano"
                name="cuerpo_texto"
                multiline
                rows={7}
                fullWidth
                value={form.cuerpo_texto}
                onChange={handleChange}
                disabled={saving || (isEditing && !canEdit) || (!isEditing && !canCreate)}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="HTML"
                name="cuerpo_html"
                multiline
                rows={8}
                fullWidth
                value={form.cuerpo_html}
                onChange={handleChange}
                disabled={saving || (isEditing && !canEdit) || (!isEditing && !canCreate)}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.activo}
                    onChange={handleSwitch}
                    disabled={saving || (isEditing && !canEdit) || (!isEditing && !canCreate)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: dashboardTheme.primary_color,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: dashboardTheme.primary_color,
                      },
                    }}
                  />
                }
                label="Plantilla activa"
                sx={{
                  color: dashboardTheme.text_color,
                  "& .MuiFormControlLabel-label": {
                    fontSize: 14,
                    fontWeight: 600,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={saving || (isEditing && !canEdit) || (!isEditing && !canCreate)}
                  startIcon={isEditing ? <SaveRoundedIcon /> : <AddRoundedIcon />}
                  sx={primaryButtonSx}
                >
                  {saving
                    ? "Guardando..."
                    : isEditing
                    ? "Actualizar plantilla"
                    : "Guardar plantilla"}
                </Button>

                {isEditing && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={saving}
                    startIcon={<CloseRoundedIcon />}
                    sx={secondaryButtonSx}
                  >
                    Cancelar edición
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: "14px",
          border: `1px solid ${dashboardTheme.border_color}`,
          bgcolor: dashboardTheme.card_background,
          boxShadow:
            dashboardTheme.theme_mode === "dark"
              ? "none"
              : "0 4px 10px rgba(0,0,0,0.08)",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 16, md: 17 },
            fontWeight: 600,
            color: dashboardTheme.text_color,
            mb: 2,
          }}
        >
          Plantillas registradas
        </Typography>

        {plantillas.length === 0 ? (
          <Typography
            sx={{
              color: dashboardTheme.muted_text_color,
              fontSize: 14,
            }}
          >
            No hay plantillas registradas.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {plantillas.map((template) => (
              <Paper
                key={template.id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border:
                    form.id === template.id
                      ? `1px solid ${dashboardTheme.primary_color}`
                      : `1px solid ${dashboardTheme.border_color}`,
                  bgcolor:
                    dashboardTheme.theme_mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : "#ffffff",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={1.5}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: dashboardTheme.text_color,
                        mb: 0.4,
                      }}
                    >
                      {template.nombre}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13,
                        color: dashboardTheme.muted_text_color,
                        mb: 0.7,
                      }}
                    >
                      {getTipoLabel(template.tipo)}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 13.5,
                        color: dashboardTheme.text_color,
                        wordBreak: "break-word",
                      }}
                    >
                      {template.asunto}
                    </Typography>

                    <Chip
                      label={template.activo ? "Activa" : "Inactiva"}
                      size="small"
                      sx={{
                        mt: 1,
                        fontWeight: 700,
                        bgcolor: template.activo
                          ? dashboardTheme.theme_mode === "dark"
                            ? "rgba(34,197,94,0.14)"
                            : "#dcfce7"
                          : dashboardTheme.theme_mode === "dark"
                          ? "rgba(239,68,68,0.14)"
                          : "#fee2e2",
                        color: template.activo ? "#16a34a" : "#dc2626",
                      }}
                    />
                  </Box>

                  <Stack direction="row" spacing={0.8}>
                    <Tooltip title="Editar plantilla">
                      <span>
                        <IconButton
                          onClick={() => editTemplate(template)}
                          disabled={!canEdit || saving || deletingId === template.id}
                          sx={iconButtonSx}
                          size="small"
                        >
                          <EditRoundedIcon sx={{ color: dashboardTheme.primary_color }} />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Eliminar plantilla">
                      <span>
                        <IconButton
                          onClick={() => deleteTemplate(template.id)}
                          disabled={!canDelete || saving || deletingId === template.id}
                          size="small"
                          sx={{
                            bgcolor:
                              dashboardTheme.theme_mode === "dark"
                                ? "rgba(239,68,68,0.1)"
                                : "#fee2e2",
                            "&:hover": {
                              bgcolor:
                                dashboardTheme.theme_mode === "dark"
                                  ? "rgba(239,68,68,0.16)"
                                  : "#fecaca",
                            },
                          }}
                        >
                          {deletingId === template.id ? (
                            <CircularProgress size={18} sx={{ color: "#dc2626" }} />
                          ) : (
                            <DeleteRoundedIcon sx={{ color: "#dc2626" }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

export default EnterpriseTemplates;